import { Alert, Button, Empty, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdDeleteOutline, MdOutlineAdd } from 'react-icons/md'

import { useNotification } from '@/Core/Hooks/useNotification'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'

import { crmOwnerOf } from '../Owner/CrmOwnerFormContext'
import { CrmChildListStore } from './CrmChildListStore'

import type { CrmOwnerFormContextLike } from '../Owner/CrmOwnerFormContext'
import type { CrmChildRow } from './CrmChildListStore'
import type { TableColumnsType } from 'antd'
import type { ReactElement, ReactNode } from 'react'

const { Text } = Typography

/** Тексти вкладки: що за записи, як про них питати й що казати, коли їх немає. */
export interface CrmChildListTexts {
    /** «Додати договір» — без плюса: іконку дає кнопка */
    add: string
    /** «Договорів ще немає.» */
    empty: string
    /** «Не вдалося завантажити договори.» */
    loadFailed: string
    /** Підтвердження видалення: питання й наслідок */
    removeTitle: string
    removeDescription: string
    /** Підпис кнопки видалення для читачів екрана: «Видалити договір» */
    removeLabel: string
    /** «договорів» — у рядку «Показано N договорів із M» */
    countNoun: string
}

export interface CrmChildListTabProps<T extends CrmChildRow> {
    /** Ключ ресурсу дитини: `Crm.ClientContract` */
    resource: string
    /** Колекція дітей: `/services/client_contracts` */
    restUrl: string
    /** Поле власника в дитині: `client`, `carrier` */
    ownerField: string
    /** `formContext` картки власника — звідти `uuid` і `@id` */
    ownerFormContext: CrmOwnerFormContextLike | undefined
    /** Чи показувати «Додати»: право вирішує вкладка-обгортка (`SPEC-008` §7.1) */
    canAdd: boolean
    /** Колонки таблиці; колонку дій вкладка додає сама */
    columns: TableColumnsType<T>
    texts: CrmChildListTexts
    /**
     * Власна кнопка додавання замість типової (за того самого `canAdd`):
     * «Друковані форми» додають до неї меню генерації (`SPEC-012` §9.2).
     * `create` відкриває картку нового запису, `reload` — перечитує список.
     */
    renderAdd?: (actions: { create: () => void; reload: () => void }) => ReactNode
}

/**
 * Вкладка-список дітей картки власника CRM (`SPEC-008` §7.1): договори,
 * документи. Обгортки дають ресурс, колонки, тексти й право «Додати».
 *
 * ⚠️ Власна таблиця, а не ядровий `ResourceList` ресурсу дитини: вкладений
 * список **чужого** ресурсу в картці підхоплює її контекст, і збереження
 * дитини пішло б на адресу власника (`.claude/docs/tasks/CORE-ISSUES.md` §3).
 * Картку дитини відкриває `OpenResourceCardTask` — поза контекстом власника,
 * другою шухлядою поверх його картки.
 *
 *  - **«Додати»** — власник у новому записі вже стоїть (`initialValues`),
 *    вибору власника у формі немає. З інших місць дітей не створюють
 *    (`CREATED_FROM_PARENT`);
 *  - **рядок** відкриває картку дитини, «Видалити» — за `_permissions` рядка й
 *    лише через `Popconfirm`;
 *  - список перечитується після закриття картки — зміни могли бути внесені
 *    не лише кнопкою «Зберегти» (файли документа вантажаться одразу).
 */
const CrmChildListTabView = observer(
    <T extends CrmChildRow>({
        resource,
        restUrl,
        ownerField,
        ownerFormContext,
        canAdd,
        columns,
        texts,
        renderAdd,
    }: CrmChildListTabProps<T>) => {
        const [store] = useState(() => new CrmChildListStore<T>(restUrl, ownerField))
        const { showError } = useNotification()

        const owner = crmOwnerOf(ownerFormContext)
        const ownerUuid = owner?.uuid

        useEffect(() => {
            void store.load(ownerUuid)
        }, [store, ownerUuid])

        if (!owner) return null

        const reload = () => store.load(owner.uuid)

        const openCard = (mode: 'create' | 'view', uuid?: string): void => {
            OpenResourceCardTask({
                resource,
                mode,
                displayMode: 'drawer',
                ...(uuid ? { primaryFieldValue: uuid } : {}),
                ...(mode === 'create' ? { initialValues: { [ownerField]: owner } } : {}),
                onSave: reload,
                onClose: () => {
                    void reload()
                },
            })
        }

        const remove = (row: T): void => {
            void store.remove(row.uuid, owner.uuid).then((error) => {
                if (error !== null) showError('Помилка', error)
            })
        }

        const tableColumns: TableColumnsType<T> = [
            ...columns,
            {
                title: '',
                key: 'actions',
                width: 56,
                render: (_: unknown, row: T) =>
                    row._permissions?.delete === true ? (
                        // Клік у колонці дій не відкриває картку рядка
                        <span onClick={(event) => event.stopPropagation()}>
                            <Popconfirm
                                title={texts.removeTitle}
                                description={texts.removeDescription}
                                okText="Видалити"
                                cancelText="Скасувати"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => remove(row)}
                            >
                                <Tooltip title="Видалити">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<MdDeleteOutline />}
                                        aria-label={texts.removeLabel}
                                        loading={store.removingUuid === row.uuid}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        </span>
                    ) : null,
            },
        ]

        const hidden = store.total - store.items.length

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {canAdd && renderAdd ? (
                    renderAdd({ create: () => openCard('create'), reload: () => void reload() })
                ) : canAdd ? (
                    <Button type="primary" icon={<MdOutlineAdd />} onClick={() => openCard('create')}>
                        {texts.add}
                    </Button>
                ) : null}

                {store.failed ? <Alert type="error" showIcon title={texts.loadFailed} /> : null}

                {store.isLoading && store.items.length === 0 ? (
                    <Spin style={{ display: 'block', margin: '40px auto' }} />
                ) : store.items.length === 0 && !store.failed ? (
                    <Empty description={texts.empty} />
                ) : (
                    <Table<T>
                        size="small"
                        rowKey="uuid"
                        pagination={false}
                        columns={tableColumns}
                        dataSource={store.items}
                        loading={store.isLoading}
                        onRow={(row) => ({
                            style: { cursor: 'pointer' },
                            onClick: () => openCard('view', row.uuid),
                        })}
                    />
                )}

                {hidden > 0 ? (
                    <Text type="secondary">
                        Показано {store.items.length} {texts.countNoun} із {store.total}
                    </Text>
                ) : null}
            </Space>
        )
    }
)

/** `observer` стирає параметр типу — повертаємо його, щоб колонки знали свій рядок. */
export const CrmChildListTab = CrmChildListTabView as <T extends CrmChildRow>(
    props: CrmChildListTabProps<T>
) => ReactElement | null
